const TX_ID_PREFIX = "tx-";
const INVOICE_ID_PREFIX = "inv-";

export function parseTransactionMatchId(matchableId: string): number {
    const raw = matchableId.replace(TX_ID_PREFIX, "");
    const id = Number(raw);
    if (!Number.isFinite(id)) {
        throw new Error(`Invalid transaction match id: ${matchableId}`);
    }
    return id;
}

/** Like `parseTransactionMatchId`, but null for ids that aren't a transaction's. */
export function findTransactionMatchId(matchableId: string): number | null {
    if (!matchableId.startsWith(TX_ID_PREFIX)) {
        return null;
    }
    const id = Number(matchableId.slice(TX_ID_PREFIX.length));
    return Number.isInteger(id) ? id : null;
}

export function parseInvoiceMatchId(matchableId: string): string {
    return matchableId.replace(INVOICE_ID_PREFIX, "");
}

import { InvoiceBase } from "@common/types/invoice";
import { BillBase } from "@common/types/bill";
import { toUTCDateOnly } from "@common/helpers/date";
import { getEffectiveGeneralLedgerAccountId } from "@common/helpers/transactions";
import {
    isInvoiceAllocationLine,
    logicalInvoiceLineAmount,
    logicalInvoiceLineMatchedAmount,
} from "@common/helpers/invoice-line-allocation";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { AccrualMatchSuggestionDisplay } from "@common/schemas/domains/accrualReview/accrual-match-suggestion-display.schema";
import { AccrualMatchedTransactionDisplay } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { Entity } from "@common/types/entity";
import { toTransactionPreviewData } from "@components/transactions/TransactionPreview/TransactionPreview";
import {
    billLineExpenseAccounts,
    collectBillExpenseAccounts,
    collectInvoiceLedgerAccounts,
} from "./invoiceLedgerAccountIds";
import {
    MatchableItem,
    MatchableLineItem,
    MatchableTransaction,
    MatchItemType,
} from "./types";

export function transactionToMatchableItem({
    transaction,
    accrualAccountId,
    isCashBasis = false,
    entityIdForGLAccount,
}: {
    transaction: MatchableTransaction;
    accrualAccountId?: string | null;
    isCashBasis?: boolean;
    /**
     * Entity whose ledger owns the transaction's journal entries. Differs from
     * `transaction.entity.id` for a non-business transaction sitting in a
     * business financial account; callers that render the account against a
     * chart of accounts fetched for that entity must pass it, or the column
     * resolves against a ledger that holds none of the entries.
     */
    entityIdForGLAccount?: number;
}): MatchableItem {
    // The account column reflects the matching basis: cash-basis matching
    // shows the transaction's own cash ledger account, while accrual matching
    // shows the accrual ledger account only. Gating on `isCashBasis` keeps the
    // accrual flow from populating the "Accrual ledger" column with a cash
    // account when a transaction has no accrual account.
    //
    // For cash basis we resolve the account the same way the Transactions Feed
    // does, falling back to the non-financial cash-basis journal entry line
    // when `accountId` is not hydrated (no override row). Otherwise the column
    // would be blank for transactions whose cash GL account only exists in
    // their journal entries.
    const effectiveAccountId = isCashBasis
        ? getEffectiveGeneralLedgerAccountId({
              amount: transaction.amount,
              accountId: transaction.accountId ?? null,
              entityId: entityIdForGLAccount ?? transaction.entity.id,
              journalEntries: transaction.journalEntries,
          })
        : (transaction.accrualAccountId ?? accrualAccountId ?? null);

    // Resolved independently of `isCashBasis` (unlike `effectiveAccountId`
    // above) so the default match drawer view's dedicated "Cash ledger" and
    // "Accrual ledger" columns (PRO-12048) can render both side by side for
    // dual-ledger workspaces, rather than the single basis-swapped `ACCOUNT`
    // column the legacy views (allocation drawer, audit log) still use.
    const effectiveCashAccountId = getEffectiveGeneralLedgerAccountId({
        amount: transaction.amount,
        accountId: transaction.accountId ?? null,
        entityId: entityIdForGLAccount ?? transaction.entity.id,
        journalEntries: transaction.journalEntries,
    });
    const effectiveAccrualAccountId =
        transaction.accrualAccountId ?? accrualAccountId ?? null;

    return {
        id: `tx-${transaction.id}`,
        date: transaction.date,
        dateOnly: transaction.dateOnly,
        type: MatchItemType.TRANSACTION,
        counterpartyName: transaction.counterparty?.name ?? null,
        description:
            transaction.displayDescription ?? transaction.bankDescription,
        bankDescription: transaction.bankDescription,
        accountIds: effectiveAccountId ? [effectiveAccountId] : [],
        cashAccountIds: effectiveCashAccountId ? [effectiveCashAccountId] : [],
        accrualAccountIds: effectiveAccrualAccountId
            ? [effectiveAccrualAccountId]
            : [],
        categoryId: transaction.categoryId ?? null,
        pairedTransferId: transaction.pairedTransferId ?? null,
        matchPreview: toTransactionPreviewData(transaction.pairedTransfer),
        classIds: transaction.classAssignments?.map((c) => c.classId) ?? [],
        amount: transaction.amount,
        openAmount: null,
        entityId: transaction.entity.id,
        // The GL accounts above resolve against `entityIdForGLAccount` (the
        // ledger that holds the transaction's journal entries), which differs
        // from the display `entityId` for a non-business transaction in a
        // business financial account. The Cash/Accrual ledger cells must load
        // the chart for this entity, not `entityId`, or the cell goes blank.
        ledgerEntityId: entityIdForGLAccount ?? transaction.entity.id,
        entity: transaction.entity,
        financialAccountId: transaction.financialAccount?.id ?? null,
    };
}

const UNKNOWN_ENTITY = {
    id: 0,
    name: "",
    iconColor: "",
    workspaceId: "",
    logo: null,
};

interface AccountOverrideRef {
    accountingLedgerId?: string;
    accountId: string;
}

interface AggregableLine {
    accountId?: string | null;
    accountsReceivableId?: string | null;
    accountsPayableId?: string | null;
    accountOverrides?: AccountOverrideRef[];
    accountsReceivableOverrides?: AccountOverrideRef[];
    classAssignments?: Array<{ classId: string }>;
    matchedAmount: number;
    kind?: string | null;
    rowId?: string | null;
}

type MatchableDocumentEntity = Pick<
    Entity,
    "id" | "name" | "iconColor" | "workspaceId" | "logo"
>;

interface MatchableDocumentAccount {
    entityId: number | null;
    entity?: MatchableDocumentEntity | null;
}

type MatchableInvoiceDocument = Pick<
    InvoiceBase,
    | "id"
    | "createdAt"
    | "description"
    | "total"
    | "financialAccountId"
    | "entityId"
    | "source"
> & {
    invoiceNumber?: string | null;
    dateOnly?: string | null;
    counterparty?: { name: string } | null;
    lines?: Array<
        AggregableLine & {
            id: string;
            description: string | null;
            amount: number;
        }
    >;
    financialAccount?: MatchableDocumentAccount | null;
};

type MatchableBillDocument = Pick<
    BillBase,
    | "id"
    | "createdAt"
    | "description"
    | "total"
    | "financialAccountId"
    | "entityId"
> & {
    dateOnly?: string | null;
    counterparty?: { name: string } | null;
    lines?: Array<
        AggregableLine & {
            id: string;
            description: string | null;
            amount: number;
        }
    >;
    financialAccount?: MatchableDocumentAccount | null;
};

type CurrentTxLineItemMatchAmounts = ReadonlyMap<string, number>;

function adjustedMatchedAmount(
    lineItemId: string,
    matchedAmount: number,
    currentTxMatchAmounts?: CurrentTxLineItemMatchAmounts,
): number {
    const refund = currentTxMatchAmounts?.get(lineItemId) ?? 0;
    // Guard against stale cache or data inconsistency where the refund
    // exceeds the line's matchedAmount — otherwise openAmount would
    // exceed the line's amount and inflate greedy allocations downstream.
    return Math.max(0, matchedAmount - refund);
}

function withAdjustedMatchedAmounts<
    T extends { id: string; matchedAmount: number },
>(lines: T[], currentTxMatchAmounts?: CurrentTxLineItemMatchAmounts): T[] {
    return lines.map((line) => ({
        ...line,
        matchedAmount: adjustedMatchedAmount(
            line.id,
            line.matchedAmount,
            currentTxMatchAmounts,
        ),
    }));
}

function mapLineItem({
    li,
    accountField,
    currentTxMatchAmounts,
    allLines = [],
}: {
    li: AggregableLine & {
        id: string;
        description: string | null;
        amount: number;
    };
    accountField: "accountsReceivableId" | "accountsPayableId";
    currentTxMatchAmounts?: CurrentTxLineItemMatchAmounts;
    allLines?: Array<AggregableLine & { id: string; amount: number }>;
}): MatchableLineItem {
    const matched = logicalInvoiceLineMatchedAmount({
        itemId: li.id,
        itemMatchedAmount: adjustedMatchedAmount(
            li.id,
            li.matchedAmount,
            currentTxMatchAmounts,
        ),
        lines: withAdjustedMatchedAmounts(allLines, currentTxMatchAmounts),
    });
    const amount = logicalInvoiceLineAmount({
        itemId: li.id,
        itemAmount: li.amount,
        lines: allLines,
    });
    return {
        id: li.id,
        description: li.description,
        accountId: li[accountField] ?? null,
        classIds: li.classAssignments?.map((c) => c.classId) ?? [],
        amount,
        openAmount: amount - matched,
    };
}

function aggregateLines(
    lines: Array<AggregableLine & { id: string; amount: number }>,
    currentTxMatchAmounts?: CurrentTxLineItemMatchAmounts,
) {
    const allocationLines = lines.filter(isInvoiceAllocationLine);
    const accountIds = [
        ...new Set(
            allocationLines
                .map((l) => l.accountsReceivableId ?? l.accountsPayableId)
                .filter((id): id is string => id != null),
        ),
    ];
    const classIds = [
        ...new Set(
            allocationLines.flatMap(
                (l) => l.classAssignments?.map((c) => c.classId) ?? [],
            ),
        ),
    ];
    const adjustedLines = withAdjustedMatchedAmounts(
        lines,
        currentTxMatchAmounts,
    );
    const matchedTotal = allocationLines.reduce(
        (sum, l) =>
            sum +
            logicalInvoiceLineMatchedAmount({
                itemId: l.id,
                itemMatchedAmount: adjustedMatchedAmount(
                    l.id,
                    l.matchedAmount,
                    currentTxMatchAmounts,
                ),
                lines: adjustedLines,
            }),
        0,
    );
    return { accountIds, classIds, matchedTotal };
}

function joinUniqueLineDescriptions(
    lines: Array<{ description: string | null }>,
): string | null {
    const descriptions = [
        ...new Set(
            lines
                .map((l) => l.description?.trim())
                .filter((d): d is string => !!d),
        ),
    ];
    if (descriptions.length === 0) {
        return null;
    }
    return descriptions.join(" / ");
}

export function invoiceToMatchableItem(
    invoice: MatchableInvoiceDocument,
    currentTxMatchAmounts?: CurrentTxLineItemMatchAmounts,
): MatchableItem {
    const lines = invoice.lines ?? [];
    const allocationLines = lines.filter(isInvoiceAllocationLine);
    const { accountIds, classIds, matchedTotal } = aggregateLines(
        lines,
        currentTxMatchAmounts,
    );
    const entity = invoice.financialAccount?.entity;
    const ledgerAccounts = collectInvoiceLedgerAccounts(lines);

    return {
        id: `${INVOICE_ID_PREFIX}${invoice.id}`,
        date: invoice.createdAt,
        dateOnly: invoice.dateOnly ?? toUTCDateOnly(invoice.createdAt),
        type: MatchItemType.INVOICE,
        counterpartyName: invoice.counterparty?.name ?? null,
        // Manually created invoices have no document-level `description`
        // (the importer only exposes per-line descriptions). Fall back
        // to the line descriptions the user actually typed; leave the
        // column empty if those are missing too (per product decision —
        // we explicitly don't fall back to `invoiceNumber`).
        description:
            invoice.description ?? joinUniqueLineDescriptions(allocationLines),
        documentNumber: invoice.invoiceNumber ?? null,
        accountIds,
        // Cash ledger lists every income-side account on the invoice,
        // including tax and discount lines. Accrual ledger lists receivables.
        cashAccountIds: ledgerAccounts.incomeAccountIds,
        missingIncomeFallbacks: ledgerAccounts.missingIncomeFallbacks,
        accrualAccountIds: ledgerAccounts.receivableAccountIds,
        usesDefaultReceivableAccount:
            ledgerAccounts.usesDefaultReceivableAccount,
        classIds,
        amount: invoice.total,
        openAmount: invoice.total - matchedTotal,
        // The document's own `entityId` is the source of truth and is
        // always set; `financialAccount.entity` is only available when
        // an account is attached. Manually created invoices have no
        // financial account, so reading entity from it leaves the column
        // blank for those rows. EntityCell resolves the full entity from
        // the workspace cache when only an id is known.
        entityId: invoice.entityId,
        entity: entity ?? UNKNOWN_ENTITY,
        financialAccountId: invoice.financialAccountId ?? null,
        invoiceSource: invoice.source,
        lineItems: allocationLines.map((li) =>
            mapLineItem({
                li,
                accountField: "accountsReceivableId",
                currentTxMatchAmounts,
                allLines: lines,
            }),
        ),
    };
}

export function billToMatchableItem(
    bill: MatchableBillDocument,
    currentTxMatchAmounts?: CurrentTxLineItemMatchAmounts,
): MatchableItem {
    const lines = bill.lines ?? [];
    const allocationLines = lines.filter(isInvoiceAllocationLine);
    const { accountIds, classIds, matchedTotal } = aggregateLines(
        lines,
        currentTxMatchAmounts,
    );
    const entity = bill.financialAccount?.entity;
    const expenseAccounts = collectBillExpenseAccounts(allocationLines);

    return {
        id: `bill-${bill.id}`,
        date: bill.createdAt,
        dateOnly: bill.dateOnly ?? toUTCDateOnly(bill.createdAt),
        type: MatchItemType.BILL,
        counterpartyName: bill.counterparty?.name ?? null,
        // See invoiceToMatchableItem above for the fallback rationale.
        description:
            bill.description ?? joinUniqueLineDescriptions(allocationLines),
        accountIds,
        // Expense accounts live on the lines (`accountOverrides`, falling
        // back to the legacy `accountId`). Payables stay on
        // `accrualAccountIds` below.
        cashAccountIds: expenseAccounts.expenseAccountIds,
        missingIncomeFallbacks: expenseAccounts.missingIncomeFallbacks,
        accrualAccountIds: accountIds,
        classIds,
        amount: bill.total,
        openAmount: bill.total - matchedTotal,
        // See invoiceToMatchableItem above for why we read entityId
        // from the document itself rather than the financial account.
        entityId: bill.entityId,
        entity: entity ?? UNKNOWN_ENTITY,
        financialAccountId: bill.financialAccountId ?? null,
        lineItems: allocationLines.map((li) => {
            const lineExpense = billLineExpenseAccounts(li);
            return {
                ...mapLineItem({
                    li,
                    accountField: "accountsPayableId",
                    currentTxMatchAmounts,
                    allLines: lines,
                }),
                cashAccountIds: lineExpense.expenseAccountIds,
                missingIncomeFallbacks: lineExpense.missingIncomeFallbacks,
            };
        }),
    };
}

export function suggestionToMatchableItem(
    suggestion: AccrualMatchSuggestionDisplay,
    entity: TransactionListItem["entity"],
): MatchableItem | null {
    if (suggestion.matchedTransaction) {
        const tx = suggestion.matchedTransaction;
        return transactionToMatchableItem({
            transaction: {
                id: tx.id,
                amount: tx.amount,
                date: tx.date,
                bankDescription: tx.bankDescription,
                displayDescription: tx.displayDescription,
                isBusiness: true,
                entity,
            } as MatchableTransaction,
        });
    }

    const doc = suggestion.invoice ?? suggestion.bill;
    if (!doc) {
        return null;
    }

    const isInvoice = !!suggestion.invoice;
    const suggestedAccountIds = doc.lineItemAccountId
        ? [doc.lineItemAccountId]
        : [];
    return {
        id: `${isInvoice ? "inv" : "bill"}-${doc.id}`,
        date: doc.createdAt,
        type: isInvoice ? MatchItemType.INVOICE : MatchItemType.BILL,
        counterpartyName: doc.counterpartyName ?? null,
        description: doc.invoiceNumber,
        accountIds: suggestedAccountIds,
        // Placeholder until useEnrichedSuggestedItems replaces this with
        // invoiceToMatchableItem / billToMatchableItem. Those mappers own
        // the income and expense accounts; this payload only has the
        // accrual account id.
        cashAccountIds: [],
        accrualAccountIds: suggestedAccountIds,
        classIds: [],
        amount: doc.total,
        openAmount: doc.openAmount ?? null,
        entityId: entity.id,
        entity,
        // Suggestion payloads don't carry financial account info; the column
        // is left blank for items mapped via this path.
        financialAccountId: null,
    };
}

export function accrualMatchedTransactionToMatchableItem(
    match: AccrualMatchedTransactionDisplay,
): MatchableItem {
    const accrualAccountIds = match.accrualAccountId
        ? [match.accrualAccountId]
        : [];
    return {
        id: `tx-${match.transactionId}`,
        date: match.date,
        dateOnly: match.dateOnly ?? null,
        type: MatchItemType.TRANSACTION,
        counterpartyName: match.counterparty?.name ?? null,
        description: match.displayDescription ?? match.bankDescription,
        bankDescription: match.bankDescription,
        accountIds: accrualAccountIds,
        // Feeds the reverse-match drawer, which is an accrual-only flow (a
        // transaction matched to a doc's AR/AP), so there is no cash-side
        // account to populate here — mirrors invoiceToMatchableItem /
        // billToMatchableItem's split above.
        cashAccountIds: [],
        accrualAccountIds,
        classIds: match.classAssignments.map((c) => c.classId),
        amount: match.amount,
        openAmount: match.matchAmount,
        entityId: match.entity.id,
        entity: match.entity,
        financialAccountId: match.financialAccountId,
    };
}
